import React, { createContext, useContext, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "src/utils/AuthContext.js";
import { useSimplePayment } from "src/utils/useSimplePayment.js";
import {
  hasAccess,
  getUpgradeFlow,
} from "src/services/FeatureGatingService.js";
import SimpleUpgradeConsumerDialog from "src/components/blocks/dialogs/SimpleUpgradeConsumerDialog.js";
import { handleCustomerPortal } from "src/utils/Utils.js";
import { Box } from "@mui/material";
import LoadingAnimation from "src/components/blocks/LoadingAnimation.js";

/**
 * FeatureAccessContext
 *
 * Provides centralized feature access management with automatic simple payment
 * handling and upgrade flow navigation.
 *
 * ============================================================================
 * USAGE
 * ============================================================================
 *
 * 1. Wrap your app with FeatureAccessProvider (done in index.js)
 * 2. Use the hook in any component:
 *
 *    import { useFeatureAccess } from 'src/utils/FeatureAccessContext'
 *
 *    const { requestFeatureAccess } = useFeatureAccess();
 *
 * 3. Request feature access with action callback:
 *
 *    const handleAction = async (item) => {
 *      const performAction = () => doSomething(item);
 *
 *      const result = await requestFeatureAccess(Features.MY_FEATURE.key, {
 *        calendar: item,
 *        action: performAction,
 *        continueText: "Continue with Action",
 *      });
 *
 *      if (result.granted && result.reason === "has_access") {
 *        performAction(); // Execute immediately if already has access
 *      }
 *      // If simple payment succeeded, dialog will handle it
 *      // If not eligible, user was redirected
 *    };
 *
 * ============================================================================
 * HOW IT WORKS
 * ============================================================================
 *
 * Three Cases:
 * 1. User already has access → Returns immediately with reason: "has_access"
 * 2. User is simple payment eligible → Processes payment → Shows dialog → Returns reason: "simple_payment_success"
 * 3. User is not eligible → Redirects to upgrade page → Returns reason: "redirected_to_upgrade"
 *
 * The SimpleUpgradeConsumerDialog is rendered at the app level and managed
 * by this context, so individual components don't need to render it.
 *
 * Loading State:
 * A full-screen loading overlay is automatically shown during payment processing.
 * Components don't need to manage loading state - it's handled globally.
 *
 * Queue Management:
 * Multiple simultaneous payment requests are queued. The dialog shows each
 * action in sequence after the user clicks "Continue".
 */

const FeatureAccessContext = createContext();

export const FeatureAccessProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, refetchAuth } = useContext(AuthContext);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionQueue, setActionQueue] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const { handleSimplePayment, shouldTrySimplePayment } = useSimplePayment({
    onSuccess: async () => {},
    type: "consumer_annual",
    trialPeriodDays: 7,
  });

  /**
   * Request access to a gated feature
   * Handles: access check → simple payment → upgrade redirect
   *
   * @param {string} featureKey - Key from Features object
   * @param {Object} options
   * @param {Function} options.action - Function to execute after successful payment + dialog confirmation
   * @param {string} options.continueText - Text for dialog "Continue" button
   * @param {Object} options.calendar - Calendar context (passed to getUpgradeFlow)
   * @param {string} options.currentPath - Current path for forward URL
   * @param {string} options.handle - Calendar handle (for route params)
   * @param {string} options.calendarId - Calendar ID (for route params)
   *
   * @returns {Object} result
   * @returns {boolean} result.granted - Whether access was granted
   * @returns {string} result.reason - "has_access" | "simple_payment_success" | "redirected_to_upgrade" | "payment_in_progress"
   */
  const requestFeatureAccess = useCallback(
    async (featureKey, options = {}) => {
      // Prevent concurrent payment processing
      if (isProcessing) {
        console.warn("Payment already in progress");
        return { granted: false, reason: "payment_in_progress" };
      }

      // Step 1: Check if user already has access
      if (hasAccess(user, featureKey)) {
        return { granted: true, reason: "has_access" };
      }

      // Step 2: Try simple payment if eligible
      if (shouldTrySimplePayment(featureKey)) {
        setIsProcessing(true);
        try {
          const hasUpgraded = await handleSimplePayment({ featureKey });
          if (hasUpgraded) {
            // Payment succeeded - add action to queue
            setActionQueue((prev) => [
              ...prev,
              {
                action: options.action,
                continueText: options.continueText || "Continue",
                timestamp: Date.now(),
              },
            ]);

            setDialogOpen(true);
            return { granted: true, reason: "simple_payment_success" };
          } else {
            return { granted: false, reason: "simple_payment_failure" };
          }
        } catch (error) {
          console.error("Simple payment failed:", error);
          // Fall through to upgrade flow
        } finally {
          setIsProcessing(false);
        }
      }

      // Step 3: Navigate to upgrade flow
      const upgradeFlow = getUpgradeFlow(featureKey, options);
      if (upgradeFlow) {
        navigate(upgradeFlow.route, { state: upgradeFlow.state });
      }
      return { granted: false, reason: "redirected_to_upgrade" };
    },
    [user, isProcessing, shouldTrySimplePayment, handleSimplePayment, navigate]
  );

  /**
   * Handle dialog "Continue" button
   * Executes the pending action and manages queue
   */
  const handleDialogContinue = useCallback(async () => {
    const [currentAction, ...remainingActions] = actionQueue;

    // Execute the action
    if (currentAction?.action) {
      try {
        const user = await refetchAuth();
        currentAction.action({ user });
      } catch (error) {
        console.error("Error executing action:", error);
      }
    }

    // Handle remaining queue
    if (remainingActions.length > 0) {
      setActionQueue(remainingActions);
      // Keep dialog open for next action
    } else {
      setDialogOpen(false);
      setActionQueue([]);
    }
  }, [actionQueue, refetchAuth]);

  /**
   * Handle dialog close
   * Clears queue and closes dialog
   */
  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    setActionQueue([]);
  }, []);

  const currentAction = actionQueue[0];

  return (
    <FeatureAccessContext.Provider
      value={{ requestFeatureAccess, isProcessingPayment: isProcessing }}
    >
      {children}

      {/* Payment processing overlay - shown globally for all components */}
      {isProcessing && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <LoadingAnimation />
        </Box>
      )}

      {/* Single dialog instance for entire app */}
      <SimpleUpgradeConsumerDialog
        open={dialogOpen}
        handleClose={handleDialogClose}
        handleContinue={handleDialogContinue}
        handleManageTrial={() => handleCustomerPortal()}
        continueText={currentAction?.continueText}
      />
    </FeatureAccessContext.Provider>
  );
};

/**
 * Hook to access feature gating functionality
 * @returns {Object} { requestFeatureAccess, isProcessingPayment }
 *
 * Note: isProcessingPayment is exposed for custom UI logic, but you typically
 * don't need it since the loading overlay is shown automatically.
 */
export const useFeatureAccess = () => {
  const context = useContext(FeatureAccessContext);
  if (!context) {
    throw new Error(
      "useFeatureAccess must be used within a FeatureAccessProvider"
    );
  }
  return context;
};

export default FeatureAccessContext;
